import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
    Chip,
    SxProps,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EddyIconSvg from "SvgIcons/EddyIconSvg";
import React from "react";
import hexToRGB from "utils/hexToRGB";
import { styleClsx, sxClsx } from "utils/styleClsx";

const SMALL_FONT_SIZE = 12;
const MED_FONT_SIZE = 16;

const ChipLabel = styled(Typography)(() => ({
    color: "inherit",
    fontWeight: "inherit",
    width: "100%",
    fontSize: "inherit",
    textOverflow: "ellipsis",
    overflow: "hidden",
}));

export type AcxChipPropsIcon = "plus" | "minus" | "eddy";

type Props = {
    size: "medium" | "small";
    color:
        | "green"
        | "red"
        | "blue"
        | "lightBlue"
        | "gray"
        | "darkGray"
        | "lightGray"
        | "yellow"
        | "darkGreen"
        | "white"
        | "transparent"
        | "eddy"
        | "adverse"
        | "primary800"
        | "secondary800"
        | "orange"
        | "contact"
        | "patientJourney"
        | "beta"
        | "brandBlend";
    label: React.ReactNode;
    onDelete?: () => void;
    onClick?: (event) => void;
    icon?: AcxChipPropsIcon;
    clickable?: boolean;
    disabled?: boolean;
    style?: React.CSSProperties;
    multiToolTip?: boolean;
    wrapTooltip?: boolean;
    customTooltip?: React.ReactNode;
    id?: string;
    removeLeftLabelPadding?: boolean;
    sx?: SxProps<Theme>;
    customMaterialIcon?: React.ReactElement;
};

function AcxChip(props: Props) {
    const theme = useTheme();

    const getChipStyles = () => {
        const styles: SxProps = {
            borderRadius: theme.shape.borderRadius,
        };

        if (props.size === "small") {
            styles.height = 24;
            styles.fontSize = SMALL_FONT_SIZE;
            styles.fontWeight = "bold";
        } else if (props.size === "medium") {
            styles.height = 28;
            styles.fontSize = MED_FONT_SIZE;
            styles.fontWeight = "bold";
        }

        if (props.color === "green") {
            styles.color = theme.palette.green.main;
            styles.backgroundColor = theme.palette.green.background;
        } else if (props.color === "red") {
            styles.color = theme.palette.error[700];
            styles.backgroundColor = theme.palette.error[100];
        } else if (props.color === "blue") {
            styles.color = theme.palette.primary.main;
            styles.backgroundColor = theme.palette.blue.background;
        } else if (props.color === "lightBlue") {
            styles.color = theme.palette.primary[500];
            styles.backgroundColor = theme.palette.primary[100];
        } else if (props.color === "gray") {
            styles.color = theme.palette.gray.main;
            styles.backgroundColor = theme.palette.gray.background;
        } else if (props.color === "darkGray") {
            styles.backgroundColor = theme.palette.neutral[200];
        } else if (props.color === "lightGray") {
            styles.backgroundColor = theme.palette.neutral[100];
        } else if (props.color === "yellow") {
            styles.color = theme.palette.yellow.main;
            styles.backgroundColor = theme.palette.yellow.background;
        } else if (props.color === "darkGreen") {
            styles.color = "#14532D";
            styles.backgroundColor = "#DCFCE7";
        } else if (props.color === "white") {
            styles.color = theme.palette.black.main;
            styles.backgroundColor = theme.palette.white.main;
        } else if (props.color === "transparent") {
            styles.color = "#3564D5";
            styles.backgroundColor = "transparent";
        } else if (props.color === "adverse") {
            styles.color = theme.palette.error[900];
            styles.backgroundColor = theme.palette.error[100];
        } else if (props.color === "eddy") {
            styles.color = theme.palette.highlight.eddy.main;
            styles.backgroundColor = theme.palette.highlight.eddy.light;
        } else if (props.color === "contact") {
            styles.backgroundColor = theme.palette.primary[200];
        } else if (props.color === "primary800") {
            styles.color = theme.palette.primary[800];
            styles.backgroundColor = theme.palette.primary[200];
        } else if (props.color === "secondary800") {
            styles.color = theme.palette.secondary[800];
            styles.backgroundColor = theme.palette.secondary[100];
        } else if (props.color === "orange") {
            styles.color = theme.palette.orange.main;
            styles.backgroundColor = hexToRGB(theme.palette.orange.main, 0.25);
        } else if (props.color === "patientJourney") {
            styles.color = theme.palette.primary[700];
            styles.backgroundColor = theme.palette.primary[100];
        } else if (props.color === "beta") {
            styles.color = theme.palette.secondary[800];
            styles.backgroundColor = "transparent";
            styles.border = `1px solid ${theme.palette.secondary[800]}`;
        } else if (props.color === "brandBlend") {
            styles.color = theme.palette.white.main;
            styles.backgroundColor = theme.palette.primary.main;
            styles.backgroundImage =
                "linear-gradient(135deg, #7A30CA 0%, #239C98 100%)";
        }

        return styles;
    };

    let label;

    if (props.multiToolTip === true && typeof props.label === "string") {
        // label formatting for individual tooltips on acx chips, see tags in eval coaching table vm
        label = <ChipLabel>{props.label}</ChipLabel>;
    } else if (typeof props.label === "string" && props.wrapTooltip === true) {
        // label formatting for wrapped tooltip text, ellipsis for acx chip label, see request dispute dialog
        label = <ChipLabel>{props.label}</ChipLabel>;
    } else if (typeof props.label === "string") {
        // label formatting for no wrap, ellipsis for both
        label = <ChipLabel noWrap>{props.label}</ChipLabel>;
    } else {
        label = props.label;
    }

    var icon: React.ReactElement | undefined;
    var customIcon: React.ReactElement | undefined;

    if (props.icon === "plus") {
        icon = (
            <AddCircleOutlineIcon
                sx={sxClsx(
                    getChipStyles(),
                    props.size === "small" && {
                        fontSize: SMALL_FONT_SIZE,
                    },
                    props.size === "medium" && {
                        fontSize: MED_FONT_SIZE,
                    },
                )}
            />
        );
    }

    if (props.icon === "minus") {
        icon = (
            <RemoveCircleOutlineIcon
                sx={sxClsx(
                    getChipStyles(),
                    props.size === "small" && {
                        fontSize: SMALL_FONT_SIZE,
                    },
                    props.size === "medium" && {
                        fontSize: MED_FONT_SIZE,
                    },
                )}
            />
        );
    }

    if (props.icon === "eddy") {
        icon = (
            <EddyIconSvg
                style={styleClsx(
                    props.size === "small" && { height: "16px", width: "16px" },
                    props.size === "medium" && {
                        height: "20px",
                        width: "20px",
                    },
                )}
            />
        );
    }

    if (props.customMaterialIcon) {
        customIcon = React.cloneElement(props.customMaterialIcon, {
            style: { color: theme.palette[props.color]?.main },
        });
    }

    return (
        <Tooltip title={props.customTooltip || ""} arrow>
            <Chip
                id={props.id}
                style={props.style}
                sx={sxClsx(
                    getChipStyles(),
                    props.style,
                    {
                        marginBottom: 0.5,
                        borderTopLeftRadius: theme.shape.borderRadius,
                        borderBottomLeftRadius: theme.shape.borderRadius,
                        borderTopRightRadius: theme.shape.borderRadius,
                        borderBottomRightRadius: theme.shape.borderRadius,
                        "&MuiChip-label": {
                            padding: 0,
                            borderTopLeftRadius: theme.shape.borderRadius,
                            borderBottomLeftRadius: theme.shape.borderRadius,
                            display: "flex",
                            flexDirection: "row",
                        },
                    },
                    props.sx,
                )}
                size={props.size}
                label={label}
                disabled={props.disabled}
                onDelete={props.onDelete}
                onClick={props.onClick}
                clickable={props.clickable}
                deleteIcon={
                    <CancelIcon
                        style={{
                            fontSize:
                                props.size === "small"
                                    ? SMALL_FONT_SIZE
                                    : MED_FONT_SIZE,
                        }}
                    />
                }
                icon={icon || customIcon}
            />
        </Tooltip>
    );
}

export default AcxChip;
